import { X, AlertTriangle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Sync or async. If async, the dialog stays open until the promise settles; it only closes after success. */
  onConfirm: () => void | Promise<void>;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmButtonClassName?: string;
  /** When true, disables actions (e.g. parent mutation already running). */
  isLoading?: boolean;
  /**
   * Require the user to type this exact text before Confirm is enabled. For irreversible actions
   * where a mis-click is expensive (releasing a phone number back to the provider).
   */
  requireTypedText?: string;
  /** Label above the typed-confirmation input. */
  requireTypedLabel?: string;
  /**
   * Optional third choice, rendered between Cancel and Confirm. Sync or async (same rules as
   * onConfirm). Used for three-way prompts such as keep / unsettle / cancel.
   */
  secondaryText?: string;
  onSecondary?: () => void | Promise<void>;
  secondaryButtonClassName?: string;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmButtonClassName = 'bg-red-600 hover:bg-red-700 text-white',
  isLoading = false,
  requireTypedText,
  requireTypedLabel,
  secondaryText,
  onSecondary,
  secondaryButtonClassName = 'bg-amber-600 hover:bg-amber-700 text-white',
}: ConfirmDialogProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  const [typed, setTyped] = useState('');
  const layer = useModalLayer({
    enabled: isOpen,
    onClose,
    backNavigation: false,
  });

  useEffect(() => {
    if (!isOpen) {
      setInternalLoading(false);
      setTyped('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const buttonLoading = isLoading || internalLoading;
  const typedOk = !requireTypedText || typed.trim() === requireTypedText.trim();

  const handleConfirm = async () => {
    if (buttonLoading || !typedOk) return;
    setInternalLoading(true);
    try {
      await Promise.resolve(onConfirm());
      onClose();
    } catch {
      // Keep dialog open on failure
    } finally {
      setInternalLoading(false);
    }
  };

  const handleSecondary = async () => {
    if (buttonLoading || !onSecondary) return;
    setInternalLoading(true);
    try {
      await Promise.resolve(onSecondary());
      onClose();
    } catch {
      // Keep dialog open on failure
    } finally {
      setInternalLoading(false);
    }
  };

  // Portals to document.body so the overlay is not clipped or trapped by ancestor
  // overflow/transform stacks (common on mobile inside full-screen modals).
  const overlay = (
    <LayerDepthContext.Provider value={layer.depth}>
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 keyboard-aware-overlay"
        style={layer.style}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full keyboard-constrained-modal overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h2 id="confirm-dialog-title" className="text-xl font-semibold text-gray-900">{title}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={buttonLoading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="p-6">
            <div className="text-gray-700 whitespace-pre-line">{message}</div>
            {requireTypedText && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {requireTypedLabel ?? <>Type <span className="font-mono">{requireTypedText}</span> to confirm</>}
                </label>
                <input
                  type="text"
                  value={typed}
                  onChange={e => setTyped(e.target.value)}
                  autoFocus
                  className="w-full px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={buttonLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
            >
              {cancelText}
            </button>
            {secondaryText && onSecondary && (
              <button
                type="button"
                onClick={handleSecondary}
                disabled={buttonLoading}
                className={`px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 ${secondaryButtonClassName}`}
              >
                {secondaryText}
              </button>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={buttonLoading || !typedOk}
              className={`px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 ${confirmButtonClassName}`}
            >
              {buttonLoading ? 'Processing...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    </LayerDepthContext.Provider>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(overlay, document.body);
}
